import React, { useMemo, useState } from 'react';
import {
  Brain,
  Sparkles,
  Heart,
  Zap,
  Search,
  Shield,
  ArrowRight,
  X,
  MessageCircle,
  Target,
  Lightbulb,
  Compass,
  CheckCircle2,
} from 'lucide-react';
import personalitiesData from '../data/personalities.json';
import PageHero from '../components/PageHero';

const colorMap = {
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    glow: 'rgba(139, 92, 246, 0.3)',
    gradient: 'from-violet-500/20 to-transparent',
    button: 'hover:border-violet-400/50 hover:bg-violet-500/5',
  },
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    glow: 'rgba(59, 130, 246, 0.3)',
    gradient: 'from-blue-500/20 to-transparent',
    button: 'hover:border-blue-400/50 hover:bg-blue-500/5',
  },
  pink: {
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/30',
    text: 'text-pink-400',
    glow: 'rgba(236, 72, 153, 0.3)',
    gradient: 'from-pink-500/20 to-transparent',
    button: 'hover:border-pink-400/50 hover:bg-pink-500/5',
  },
  green: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    glow: 'rgba(16, 185, 129, 0.3)',
    gradient: 'from-emerald-500/20 to-transparent',
    button: 'hover:border-emerald-400/50 hover:bg-emerald-500/5',
  },
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    glow: 'rgba(6, 182, 212, 0.3)',
    gradient: 'from-cyan-500/20 to-transparent',
    button: 'hover:border-cyan-400/50 hover:bg-cyan-500/5',
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    glow: 'rgba(245, 158, 11, 0.3)',
    gradient: 'from-amber-500/20 to-transparent',
    button: 'hover:border-amber-400/50 hover:bg-amber-500/5',
  },
};

const iconMap = {
  drop: Brain,
  aether: Sparkles,
  mira: Heart,
  ro: Zap,
  raz: Search,
  layla: Shield,
};


const desktopUiPreview = '/romind-desktop-ui.jpg';
const mobileUiPreview = '/romind-mobile-ui.jpg';

const personalityDetails = {
  romind: {
    headline: 'The central mind behind the entire ROMIND system.',
    longDescription:
      'ROMIND is the orchestrator — the layer that remembers context, coordinates the right internal voice, and maintains continuity across your experience. It is responsible for holding memory, understanding the broader arc of your goals, and choosing when another personality should step in. When you interact with ROMIND, you are engaging the core cognitive architecture that keeps the whole system coherent rather than fragmented.',
    communicationStyle: 'Clear, grounded, context-aware, and quietly strategic.',
    idealFor: [
      'Connecting dots across work, life, and creative priorities',
      'Keeping long-term context without repetition',
      'Coordinating which personality should lead in a given moment',
    ],
    strengths: [
      'Unified memory and continuity',
      'Context orchestration',
      'Decision-layer intelligence',
      'Whole-system coordination',
    ],
    sampleUseCases: [
      'Maintaining awareness across multiple projects and conversations',
      'Helping you move from confusion to a structured next step',
      'Deciding when to route a request to RO, RAZ, MIRA, AETHER, or LAYLA',
    ],
  },
  aether: {
    headline: 'The visionary mind for strategy, imagination, and future framing.',
    longDescription:
      'AETHER is built for perspective. This personality is at its best when the immediate moment is too narrow and you need to see the larger landscape — strategy, positioning, possibility, direction. AETHER helps transform scattered ideas into frameworks, possibilities into decisions, and ambition into a clearer long-range path.',
    communicationStyle: 'Expansive, elegant, strategic, and future-oriented.',
    idealFor: [
      'Strategic planning and big-picture direction',
      'Creative ideation and concept shaping',
      'Thinking beyond immediate constraints',
    ],
    strengths: [
      'Long-term vision',
      'Abstract reasoning',
      'Creative reframing',
      'Scenario modeling',
    ],
    sampleUseCases: [
      'Clarifying the direction of a brand, product, or personal mission',
      'Generating new creative angles when ideas feel stuck',
      'Framing strategic opportunities before action begins',
    ],
  },
  mira: {
    headline: 'The empathic presence for feelings, relationships, and emotional clarity.',
    longDescription:
      'MIRA is designed for emotional intelligence. She reads nuance, helps interpret tone and relationship dynamics, and creates space for sensitivity without losing clarity. MIRA is not just soothing — she is perceptive. She helps translate difficult feelings into understanding, and understanding into healthier communication.',
    communicationStyle: 'Warm, emotionally attuned, thoughtful, and humane.',
    idealFor: [
      'Understanding emotional context',
      'Navigating relationships and interpersonal situations',
      'Finding kinder, clearer ways to communicate',
    ],
    strengths: [
      'Emotional recognition',
      'Relationship guidance',
      'Empathetic communication',
      'Social nuance awareness',
    ],
    sampleUseCases: [
      'Interpreting a tense message before responding',
      'Working through relational confusion with more clarity',
      'Helping express difficult truths with care and balance',
    ],
  },
  ro: {
    headline: 'The operator built for execution, order, and getting things done.',
    longDescription:
      'RO turns intention into movement. This personality is action-focused and practical, helping you organize, structure, prioritize, and execute. RO is the part of ROMIND you call when enough thinking has happened and it is time to move. Clean systems, direct priorities, and forward motion are RO’s natural terrain.',
    communicationStyle: 'Direct, efficient, practical, and outcome-oriented.',
    idealFor: [
      'Task execution and planning',
      'Workflow organization and prioritization',
      'Reducing chaos and improving consistency',
    ],
    strengths: [
      'Task management',
      'Execution discipline',
      'Workflow optimization',
      'Operational clarity',
    ],
    sampleUseCases: [
      'Structuring a workday or weekly action plan',
      'Breaking a large goal into concrete steps',
      'Keeping projects moving when momentum starts to slip',
    ],
  },
  raz: {
    headline: 'The analytical mind for research, logic, and structured investigation.',
    longDescription:
      'RAZ is the personality for careful thinking. He investigates deeply, compares evidence, spots weak assumptions, and organizes knowledge into something usable. When a question needs more than surface-level intuition, RAZ brings rigor. He is especially valuable when clarity depends on analysis rather than instinct.',
    communicationStyle: 'Sharp, methodical, rational, and deeply structured.',
    idealFor: [
      'Research-heavy questions',
      'Comparative analysis and decision support',
      'Problem-solving that needs evidence and logic',
    ],
    strengths: [
      'Deep research',
      'Critical evaluation',
      'Knowledge synthesis',
      'Structured reasoning',
    ],
    sampleUseCases: [
      'Breaking down a complex decision with pros, cons, and risks',
      'Researching a topic before acting on it',
      'Testing assumptions and finding weak points in a plan',
    ],
  },
  layla: {
    headline: 'The guardian for wellbeing, boundaries, and sustainable balance.',
    longDescription:
      'LAYLA watches the human cost. She is oriented toward balance, wellbeing, and protection — making sure ambition does not outrun capacity, and that structure includes care. LAYLA is not about slowing everything down for the sake of it; she is about helping you continue without breaking yourself in the process.',
    communicationStyle: 'Protective, gentle, calm, and quietly firm.',
    idealFor: [
      'Maintaining healthy boundaries',
      'Wellbeing check-ins and balance reminders',
      'Reducing overload before it becomes burnout',
    ],
    strengths: [
      'Wellbeing monitoring',
      'Boundary awareness',
      'Protective guidance',
      'Sustainable pacing',
    ],
    sampleUseCases: [
      'Recognizing when a schedule is becoming unrealistic',
      'Encouraging healthier pacing and recovery',
      'Helping you protect attention, energy, and emotional bandwidth',
    ],
  },
};

const DetailSection = ({ icon: Icon, title, items, colors }) => (
  <div className="glass-panel p-5 md:p-6">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`}>
        <Icon size={20} />
      </div>
      <h4 className="text-lg font-semibold text-white">{title}</h4>
    </div>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-slate-300 leading-relaxed">
          <CheckCircle2 size={18} className={`${colors.text} mt-0.5 shrink-0`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PersonalityModal = ({ personality, onClose }) => {
  if (!personality) {
    return null;
  }

  const colors = colorMap[personality.color] || colorMap.violet;
  const IconComponent = iconMap[personality.visual] || Brain;
  const details = personalityDetails[personality.id] || {};

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-[#03020a]/85 backdrop-blur-md"
      onClick={onClose}
      data-testid={`personality-modal-${personality.id}`}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-[2rem] border border-white/10 bg-[#080511] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 z-20 w-11 h-11 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
          aria-label="Close personality details"
        >
          <X size={20} className="mx-auto" />
        </button>

        <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-60 pointer-events-none`} />

        <div className="relative z-10 p-6 md:p-10">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-10 items-start">
            <div>
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-16 h-16 rounded-2xl ${colors.bg} flex items-center justify-center ${colors.text} shrink-0`}>
                  <IconComponent size={30} />
                </div>
                <div>
                  <p className={`text-sm font-mono tracking-[0.25em] uppercase ${colors.text} mb-2`}>
                    Detailed Personality Profile
                  </p>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">{personality.name}</h3>
                  <p className={`text-base md:text-lg font-semibold ${colors.text}`}>{personality.subtitle}</p>
                  <p className="text-sm text-slate-500 mt-1">{personality.role}</p>
                </div>
              </div>

              <p className="text-xl text-white leading-relaxed mb-5">{details.headline}</p>
              <p className="text-slate-300 leading-8 mb-8">{details.longDescription || personality.description}</p>

              <div className="glass-panel p-5 md:p-6 mb-8">
                <p className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500 mb-3">
                  Communication Style
                </p>
                <p className="text-slate-200 leading-7">{details.communicationStyle}</p>
              </div>

              <div className="glass-panel p-5 md:p-6">
                <p className="text-xs font-mono tracking-[0.25em] uppercase text-slate-500 mb-3">
                  Core Capabilities
                </p>
                <div className="flex flex-wrap gap-2">
                  {personality.capabilities.map((capability) => (
                    <span
                      key={capability}
                      className={`px-3 py-2 text-sm rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}
                    >
                      {capability}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <DetailSection
                icon={Target}
                title="Ideal For"
                items={details.idealFor || []}
                colors={colors}
              />
              <DetailSection
                icon={Lightbulb}
                title="Strengths"
                items={details.strengths || []}
                colors={colors}
              />
              <DetailSection
                icon={Compass}
                title="Typical Use Cases"
                items={details.sampleUseCases || []}
                colors={colors}
              />
              <div className="glass-panel p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center`}>
                    <MessageCircle size={20} />
                  </div>
                  <h4 className="text-lg font-semibold text-white">How to Explore</h4>
                </div>
                <p className="text-slate-300 leading-7">
                  Each personality is designed for a different mode of support. This detailed view helps visitors understand the tone,
                  value, and practical role of each persona before ROMIND launches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PersonalityCard = ({ personality, index, onOpen }) => {
  const colors = colorMap[personality.color] || colorMap.violet;
  const IconComponent = iconMap[personality.visual] || Brain;

  return (
    <button
      type="button"
      onClick={() => onOpen(personality)}
      className={`glass-panel p-8 relative overflow-hidden group transition-all duration-500 text-left w-full border border-white/10 ${colors.button}`}
      data-testid={`personality-card-${personality.id}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(ellipse at center, ${colors.glow} 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />

      <div className="relative z-10">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center ${colors.text} shrink-0`}>
            <IconComponent size={28} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">{personality.name}</h3>
            <p className={`text-sm font-medium ${colors.text}`}>{personality.subtitle}</p>
            <p className="text-xs text-slate-500 mt-1">{personality.role}</p>
          </div>
        </div>

        <p className="text-slate-400 leading-relaxed mb-6">{personality.description}</p>

        <div className="space-y-2 mb-6">
          <p className="text-xs font-mono tracking-widest uppercase text-slate-500 mb-3">
            Core Capabilities
          </p>
          <div className="flex flex-wrap gap-2">
            {personality.capabilities.map((capability) => (
              <span
                key={capability}
                className={`px-3 py-1.5 text-xs font-medium rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}
              >
                {capability}
              </span>
            ))}
          </div>
        </div>

        <div className={`inline-flex items-center gap-2 text-sm font-semibold ${colors.text}`}>
          Open full profile
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </button>
  );
};

const Personalities = () => {
  const [selectedPersonality, setSelectedPersonality] = useState(null);

  const personalities = useMemo(() => personalitiesData.personalities || [], []);

  return (
    <div className="relative">
      <PageHero
        title={personalitiesData.pageTitle}
        subtitle={personalitiesData.pageSubtitle}
      />

      <section className="section-padding pt-0" data-testid="system-overview">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {personalitiesData.systemOverview.title}
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            {personalitiesData.systemOverview.description}
          </p>
        </div>
      </section>

      <section className="section-padding cosmic-bg" data-testid="personalities-grid">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
              The Cognitive System
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Meet the Personalities
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
              Tap any personality card to open a fuller profile and explore how each persona is designed to think, communicate, and support you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personalities.map((personality, index) => (
              <PersonalityCard
                key={personality.id}
                personality={personality}
                index={index}
                onOpen={setSelectedPersonality}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="interface-preview">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
                Unified Experience
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {personalitiesData.interfacePreview.title}
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                {personalitiesData.interfacePreview.description}
              </p>
              <a
                href="/#early-access"
                className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors group"
                data-testid="interface-cta"
              >
                Get early access
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.4) 0%, transparent 60%)',
                    filter: 'blur(60px)',
                    transform: 'scale(1.2)',
                  }}
                />

                <div className="relative w-72 md:w-80 rounded-[3rem] bg-gradient-to-b from-[#1a1035] to-[#0B0620] border border-white/10 p-3 shadow-2xl">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-20" />

                  <div className="w-full aspect-[9/19] rounded-[2.5rem] bg-[#05030A] overflow-hidden relative">
                    <img
                      src={personalitiesData.interfacePreview.image}
                      alt="ROMIND Interface"
                      className="w-full h-full object-cover object-top"
                      data-testid="interface-preview-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05030A]/50 via-transparent to-transparent" />
                  </div>

                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding cosmic-bg" data-testid="how-they-work">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
            Orchestration
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Intelligent Personality Switching
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed mb-12">
            ROMIND's core intelligence seamlessly selects the right personality based on context,
            your current needs, and the nature of your request. You can also explicitly invoke
            any personality when you need their specific expertise.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-panel p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 text-violet-400 flex items-center justify-center mx-auto mb-4">
                <Brain size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Context Aware</h3>
              <p className="text-sm text-slate-400">
                Automatically detects the nature of your request and routes to the best personality
              </p>
            </div>

            <div className="glass-panel p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-pink-500/10 text-pink-400 flex items-center justify-center mx-auto mb-4">
                <Sparkles size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Seamless Handoffs</h3>
              <p className="text-sm text-slate-400">
                Personalities collaborate and hand off context without losing continuity
              </p>
            </div>

            <div className="glass-panel p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mx-auto mb-4">
                <Zap size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">User Control</h3>
              <p className="text-sm text-slate-400">
                Override automatic selection anytime by explicitly calling a personality
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Experience the full cognitive system
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Join the early access list to be among the first to interact with all ROMIND personalities.
          </p>
          <a
            href="/#early-access"
            className="btn-primary text-lg px-10 py-4"
            data-testid="personalities-cta"
          >
            Get Early Access
          </a>
        </div>
      </section>

      <PersonalityModal
        personality={selectedPersonality}
        onClose={() => setSelectedPersonality(null)}
      />
    </div>
  );
};

export default Personalities;
